import { Vector3 } from "three";
import { Viewer } from "../viewer";
import { parseDxfEdges, type EdgeSegment } from "./dxf-loader";
import { buildDxfTexture, DXF_TEX_MAX_DIST, type DxfTexture } from "./dxf-texture";
import type { PointCloudMaterial } from "../utils/PointCloudMaterial";

/**
 * DxfManager — colors the point cloud by the DXF using a GPU SPATIAL TEXTURE.
 *
 * - Built once on DXF load: a distance-field + color texture (CPU fill via a
 *   KD-tree, async, off the main thread). The texture is INDEPENDENT of the
 *   point cloud.
 * - Render: the point material shader projects each point to (East,-North),
 *   samples the texture and blends. Slider (radius/hardness/opacity) = a uniform
 *   change → O(1).
 * - Streamed tiles need NO rebuild — the shared material samples the same
 *   texture, so new points are colored automatically.
 */
export class DxfManager {
  private edges: EdgeSegment[] = [];
  private dxfTexture: DxfTexture | null = null;

  constructor(private viewer: Viewer) {}

  init() {}

  private get material(): PointCloudMaterial | undefined {
    return this.viewer._pointCloudManager?.pointMaterial as PointCloudMaterial | undefined;
  }

  async load(url: string): Promise<void> {
    this.unload();
    try {
      const edges = await parseDxfEdges(url);
      this.edges = edges;
      if (edges.length === 0) return;

      // build texture off the main thread; wired to material here (or in enable()
      // if the point material isn't ready yet).
      this.dxfTexture = await buildDxfTexture(edges);
      if (this.dxfTexture) this.wireTexture();
    } catch (e) {
      console.error("[DxfManager] DXF load failed:", e);
      throw e;
    }
  }

  /** Wire the texture + bounds into the point material uniforms (idempotent). */
  private wireTexture() {
    const mat = this.material;
    const tex = this.dxfTexture;
    if (!mat || !tex) return;
    mat.dxfTex = tex.texture;
    mat.dxfTexMin.set(tex.minX, tex.minZ);
    mat.dxfTexSpan.set(tex.spanX, tex.spanZ);
    mat.dxfMaxDist = DXF_TEX_MAX_DIST;
    mat.applyDxfUniforms();
  }

  hasTexture(): boolean {
    return !!this.dxfTexture;
  }

  /** Enable the overlay: set offset + params, turn on, push uniforms. */
  enable(offset: Vector3, radius: number, hardness: number, opacity: number) {
    const mat = this.material;
    if (!mat || !this.dxfTexture) return;
    if (!mat.dxfTex) this.wireTexture(); // material appeared after the DXF load
    mat.dxfOffset.set(offset.x, offset.y);
    mat.dxfRadius = radius;
    mat.dxfHardness = hardness;
    mat.dxfOpacity = opacity;
    mat.dxfEnabled = true;
    mat.applyDxfUniforms();
  }

  /** Change params — uniforms only, O(1), instant even with millions of points. */
  setParams(radius: number, hardness: number, opacity: number) {
    const mat = this.material;
    if (!mat) return;
    mat.dxfRadius = radius;
    mat.dxfHardness = hardness;
    mat.dxfOpacity = opacity;
    mat.applyDxfUniforms();
  }

  /** Disable the overlay. */
  disable() {
    const mat = this.material;
    if (!mat) return;
    mat.dxfEnabled = false;
    mat.applyDxfUniforms();
  }

  unload() {
    const mat = this.material;
    if (mat) {
      mat.dxfEnabled = false;
      mat.dxfTex = null;
      mat.applyDxfUniforms();
    }
    if (this.dxfTexture) {
      this.dxfTexture.texture.dispose();
      this.dxfTexture = null;
    }
    this.edges = [];
  }
}